import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, Input,
    HStack, VStack, Image, Flex, Icon, useToast, Spinner, Wrap, WrapItem, IconButton, Progress
} from '@chakra-ui/react';
import { WarningIcon, CloseIcon } from '@chakra-ui/icons';
import { FaWarehouse, FaCamera, FaImages } from 'react-icons/fa';
import { darEntradaEstoqueProblema, getSugestoesLocalizacaoDespacho, previewSemFundoImagem } from '../../services/api';
import RevisaoFotosSemFundo from './RevisaoFotosSemFundo';
import DesbloqueioProdutoCard from './DesbloqueioProdutoCard';
import { useBloqueioProduto } from './useBloqueioProduto';
import { enviarFotosProcessador } from './enviarFotosProcessador';
import { atualizarCacheImagem } from '../ProdutoImagem';
import EditorImagemModal from '../ProdutoImagensTab/EditorImagemModal';

const MIN_FOTOS = 2;

const mensagemEntrada = (res) => {
    if (res?.desbloqueado) return 'Entrada registrada e venda cancelada. Imagem corrigida e produto liberado.';
    if (res?.produto_bloqueado) return 'Entrada registrada e venda cancelada. Imagem corrigida, o produto continua bloqueado.';
    return 'Entrada em estoque registrada. Venda cancelada.';
};

const EntradaEstoqueProblemaModal = ({ isOpen, onClose, product, userId, onConfirmed }) => {
    const toast = useToast();
    const cameraInputRef = useRef(null);
    const galeriaInputRef = useRef(null);
    const [qtd, setQtd] = useState('1');
    const [loc, setLoc] = useState('');
    const [fotos, setFotos] = useState([]);
    const [sugestoes, setSugestoes] = useState([]);
    const [loadingSugestoes, setLoadingSugestoes] = useState(false);
    const [etapa, setEtapa] = useState('dados');
    const [previas, setPrevias] = useState([]);
    const [escolhas, setEscolhas] = useState({});
    const [processando, setProcessando] = useState({});
    const [preparando, setPreparando] = useState(false);
    const [imagemEditando, setImagemEditando] = useState(null);
    const [salvando, setSalvando] = useState(false);

    const precisaFotos = product?.verificationDetails?.issues?.photo === true
        && !product?.verificationDetails?.fotoCorrigidaAt;
    const maxQtd = parseInt(product?.quantity, 10) || 1;

    const {
        bloqueio, carregando: carregandoBloqueio, erro: erroBloqueio,
        desbloquear, setDesbloquear, consultar: consultarBloqueio, desbloquearEscolhido,
    } = useBloqueioProduto({
        ativo: !!isOpen && precisaFotos,
        produtoId: product?.id || null,
        vendaId: product?.vendaId || null,
        produtoEstoqueId: product?.produtoEstoqueId || null,
    });

    useEffect(() => {
        if (!isOpen || !product) return;
        setQtd(String(maxQtd));
        setLoc('');
        setFotos(prev => { prev.forEach(f => URL.revokeObjectURL(f.url)); return []; });
        setSugestoes([]);
        setEtapa('dados');
        setPrevias([]);
        setEscolhas({});
        setProcessando({});
    }, [isOpen, product?.uniqueId]);

    useEffect(() => {
        if (!isOpen || !product?.produtoEstoqueId) return;
        let cancelado = false;
        setLoadingSugestoes(true);
        getSugestoesLocalizacaoDespacho(product.produtoEstoqueId)
            .then(lista => { if (!cancelado) setSugestoes(lista); })
            .finally(() => { if (!cancelado) setLoadingSugestoes(false); });
        return () => { cancelado = true; };
    }, [isOpen, product?.produtoEstoqueId]);

    const handleAddFiles = (e) => {
        const novos = Array.from(e.target.files || [])
            .filter(f => f.type.startsWith('image/'))
            .map(f => ({ file: f, url: URL.createObjectURL(f) }));
        setFotos(prev => [...prev, ...novos]);
        e.target.value = '';
    };

    const handleRemoveFoto = (idx) => {
        setFotos(prev => {
            const alvo = prev[idx];
            if (alvo) URL.revokeObjectURL(alvo.url);
            return prev.filter((_, i) => i !== idx);
        });
    };

    const qtdNum = parseInt(qtd, 10);
    const fotosOk = !precisaFotos || fotos.length >= MIN_FOTOS;
    const podeConfirmar = qtdNum >= 1 && !!loc.trim() && fotosOk && !salvando;

    const escolhaPadrao = (previa) => (previa.url_sem_fundo && !previa.alerta ? previa.caminho_sem_fundo : null);

    const handleContinuar = async () => {
        if (!podeConfirmar) return;
        setPreparando(true);
        setPrevias(fotos.map((_, idx) => ({ chave: idx, estado: 'fila' })));
        setEscolhas({});
        setEtapa('revisao');
        try {
            const resultados = await enviarFotosProcessador({
                fotos,
                produtoId: product.id,
                onEstado: (idx, estado) => setPrevias(prev => prev.map(p => (p.chave === idx ? { ...p, estado } : p))),
                onPrevia: (idx, previa) => {
                    setPrevias(prev => prev.map(p => (p.chave === idx ? { ...previa, chave: idx, estado: 'pronto' } : p)));
                    if (previa?.imagem_id) {
                        setEscolhas(prev => ({ ...prev, [previa.imagem_id]: escolhaPadrao(previa) }));
                    }
                },
            });

            const validas = resultados.filter(r => r?.imagem_id);
            setPrevias(validas.map((previa, idx) => ({ ...previa, chave: idx, estado: 'pronto' })));

            if (validas.length < MIN_FOTOS) {
                toast({ title: 'Falha ao enviar as fotos. Tente novamente.', status: 'error', isClosable: true });
                setEtapa('dados');
            } else if (validas.length < resultados.length) {
                const fora = resultados.length - validas.length;
                toast({
                    title: fora > 1
                        ? `${fora} fotos não subiram e ficaram de fora. Volte e envie de novo se precisar.`
                        : '1 foto não subiu e ficou de fora. Volte e envie de novo se precisar.',
                    status: 'warning',
                    position: 'top',
                    duration: 6000,
                    isClosable: true,
                });
            }
        } catch (err) {
            toast({ title: 'Erro ao preparar as fotos. Tente novamente.', status: 'error', isClosable: true });
            setEtapa('dados');
        } finally {
            setPreparando(false);
        }
    };

    const handleEscolher = (imagemId, usarSemFundo) => {
        const previa = previas.find(p => p.imagem_id === imagemId);
        setEscolhas(prev => ({ ...prev, [imagemId]: usarSemFundo ? (previa?.caminho_sem_fundo || null) : null }));
    };

    const handleEditar = (previa) => {
        setImagemEditando({
            id: previa.imagem_id,
            url: previa.url_base_edicao || previa.url_original,
            url_original: null,
            deleted_at: null,
        });
    };

    const handleEditada = async (imagemAtualizada, { removeuFundo }) => {
        const imagemId = imagemEditando?.id;
        setImagemEditando(null);
        if (!imagemId) return;

        if (removeuFundo) {
            setPrevias(prev => prev.map(p => (p.imagem_id === imagemId
                ? { ...p, url_original: imagemAtualizada.url, url_base_edicao: imagemAtualizada.url_original || imagemAtualizada.url, url_sem_fundo: null, caminho_sem_fundo: null, alerta: null, editada: true }
                : p)));
            setEscolhas(prev => ({ ...prev, [imagemId]: null }));
            return;
        }

        setProcessando(prev => ({ ...prev, [imagemId]: true }));
        try {
            const previa = await previewSemFundoImagem({ imagem_id: imagemId });
            if (previa?.error) {
                toast({ title: previa.error, status: 'error', isClosable: true });
                return;
            }
            setPrevias(prev => prev.map(p => (p.imagem_id === imagemId ? { ...previa, chave: p.chave, estado: 'pronto', editada: true } : p)));
            setEscolhas(prev => ({ ...prev, [imagemId]: escolhaPadrao(previa) }));
        } finally {
            setProcessando(prev => ({ ...prev, [imagemId]: false }));
        }
    };

    const handleTentarOutroModo = async (imagemId, modelo) => {
        setProcessando(prev => ({ ...prev, [imagemId]: true }));
        try {
            const previa = await previewSemFundoImagem({ imagem_id: imagemId, modelo });
            if (previa?.error) {
                toast({ title: previa.error, status: 'error', isClosable: true });
                return;
            }
            setPrevias(prev => prev.map(p => (p.imagem_id === imagemId ? { ...previa, chave: p.chave, estado: 'pronto' } : p)));
            setEscolhas(prev => ({ ...prev, [imagemId]: escolhaPadrao(previa) }));
        } finally {
            setProcessando(prev => ({ ...prev, [imagemId]: false }));
        }
    };

    const handleConfirmar = async () => {
        if (!podeConfirmar) return;
        setSalvando(true);
        try {
            const imagemIds = previas.filter(p => p.imagem_id).map(p => p.imagem_id);
            const semFundo = {};
            Object.entries(escolhas).forEach(([imagemId, caminho]) => {
                if (caminho) semFundo[imagemId] = caminho;
            });

            const res = await darEntradaEstoqueProblema({
                venda_id: product.vendaId,
                produto_estoque_id: product.produtoEstoqueId,
                qtd: qtdNum,
                loc: loc.trim(),
                imagem_ids: imagemIds,
                sem_fundo: semFundo,
                ...(precisaFotos ? { desbloquear: desbloquearEscolhido } : {}),
                userId,
            });

            if (res?.success) {
                toast({ title: mensagemEntrada(res), status: 'success', duration: 3000, isClosable: true });
                atualizarCacheImagem(product.id, null);
                if (onConfirmed) onConfirmed(product);
                onClose();
            } else {
                toast({ title: res?.error || 'Erro ao dar entrada no estoque', status: 'error', isClosable: true });
            }
        } catch (err) {
            toast({ title: 'Erro ao processar. Tente novamente.', status: 'error', isClosable: true });
        } finally {
            setSalvando(false);
        }
    };

    if (!product) return null;

    const ocupado = preparando || salvando;
    const prontas = previas.filter(p => p.estado === 'pronto' || (!p.estado && p.imagem_id)).length;

    return (
        <>
        <Modal isOpen={isOpen} onClose={ocupado ? () => {} : onClose} isCentered size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent mx={{ base: 0, md: 4 }}>
                <ModalHeader fontSize={{ base: 'sm', md: 'md' }} pr={10} wordBreak="break-word">
                    {preparando
                        ? `Preparando fotos (${prontas} de ${previas.length})`
                        : `Dar entrada em estoque - Venda #${product.vendaId}`}
                    {preparando && (
                        <Progress
                            mt={2}
                            size="xs"
                            colorScheme="purple"
                            borderRadius="full"
                            value={previas.length ? (prontas / previas.length) * 100 : 0}
                            hasStripe
                            isAnimated
                        />
                    )}
                </ModalHeader>
                {!ocupado && <ModalCloseButton />}
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Box p={3} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
                            <Text fontWeight="semibold" fontSize="sm">{product.name}</Text>
                            {product.globalCode && (
                                <Text fontSize="xs" color="gray.600">Global: {product.globalCode}</Text>
                            )}
                            {product.brand && (
                                <Text fontSize="xs" color="gray.600">Marca: {product.brand}</Text>
                            )}
                            <Text fontSize="xs" color="red.600" mt={1}>
                                O item entra no estoque e a venda é marcada como CANCELADA.
                            </Text>
                        </Box>

                        <VStack spacing={4} align="stretch" display={etapa === 'revisao' ? 'none' : 'flex'}>
                        <FormControl isRequired>
                            <FormLabel fontSize="sm">
                                Quantidade <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal">(máx. {maxQtd})</Text>
                            </FormLabel>
                            <Input
                                type="number"
                                min={1}
                                max={maxQtd}
                                value={qtd}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    if (isNaN(v) || v < 1) { setQtd('1'); return; }
                                    if (v > maxQtd) { setQtd(String(maxQtd)); return; }
                                    setQtd(String(v));
                                }}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Localização no estoque</FormLabel>
                            {loadingSugestoes ? (
                                <Flex align="center" gap={2} mb={2}>
                                    <Spinner size="xs" color="blue.400" />
                                    <Text fontSize="xs" color="gray.500">Buscando localizações...</Text>
                                </Flex>
                            ) : sugestoes.length > 0 && (
                                <Box bg="blue.50" borderRadius="md" p={3} mb={2} borderWidth="1px" borderColor="blue.100">
                                    <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={2}>
                                        Produto já em estoque nos locais:
                                    </Text>
                                    <HStack flexWrap="wrap" spacing={2}>
                                        {sugestoes.map((s, i) => (
                                            <Button
                                                key={i}
                                                size="xs"
                                                colorScheme={loc === s.localizacao ? 'green' : 'blue'}
                                                variant={loc === s.localizacao ? 'solid' : 'outline'}
                                                onClick={() => setLoc(s.localizacao)}
                                            >
                                                {s.localizacao}: {s.quantidade} un
                                            </Button>
                                        ))}
                                    </HStack>
                                </Box>
                            )}
                            <Input
                                placeholder="Ex: A1, B2, Prateleira 3..."
                                value={loc}
                                onChange={(e) => setLoc(e.target.value)}
                            />
                        </FormControl>

                        {precisaFotos && (
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Fotos do item físico</FormLabel>
                                <Input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    multiple
                                    display="none"
                                    onChange={handleAddFiles}
                                />
                                <Input
                                    ref={galeriaInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    display="none"
                                    onChange={handleAddFiles}
                                />
                                <HStack spacing={2}>
                                    <Button
                                        size="sm"
                                        leftIcon={<Icon as={FaCamera} />}
                                        onClick={() => cameraInputRef.current?.click()}
                                        colorScheme="blue"
                                        variant="outline"
                                    >
                                        Tirar foto
                                    </Button>
                                    <Button
                                        size="sm"
                                        leftIcon={<Icon as={FaImages} />}
                                        onClick={() => galeriaInputRef.current?.click()}
                                        variant="outline"
                                    >
                                        Da galeria
                                    </Button>
                                </HStack>
                                {fotos.length > 0 && (
                                    <Wrap mt={3} spacing={2}>
                                        {fotos.map((f, idx) => (
                                            <WrapItem key={idx} position="relative">
                                                <Image src={f.url} alt={`Foto ${idx + 1}`} boxSize="64px" objectFit="cover" borderRadius="md" borderWidth="1px" borderColor="gray.200" />
                                                <IconButton
                                                    aria-label="Remover foto"
                                                    icon={<CloseIcon boxSize={2} />}
                                                    size="xs"
                                                    colorScheme="red"
                                                    borderRadius="full"
                                                    position="absolute"
                                                    top="-6px"
                                                    right="-6px"
                                                    onClick={() => handleRemoveFoto(idx)}
                                                />
                                            </WrapItem>
                                        ))}
                                    </Wrap>
                                )}
                                {!fotosOk && (
                                    <Flex align="center" gap={2} mt={2} color="red.600">
                                        <WarningIcon boxSize={3} />
                                        <Text fontSize="xs">
                                            Adicione ao menos {MIN_FOTOS} fotos de ângulos diferentes para continuar
                                        </Text>
                                    </Flex>
                                )}
                            </FormControl>
                        )}
                        </VStack>

                        {etapa === 'revisao' && (
                            <RevisaoFotosSemFundo
                                previas={previas}
                                escolhas={escolhas}
                                processando={processando}
                                onEscolher={handleEscolher}
                                onTentarOutroModo={handleTentarOutroModo}
                                onEditar={handleEditar}
                            />
                        )}

                        {precisaFotos && (
                            <DesbloqueioProdutoCard
                                carregando={carregandoBloqueio}
                                erro={erroBloqueio}
                                bloqueio={bloqueio}
                                desbloquear={desbloquear}
                                onChange={setDesbloquear}
                                onTentarNovamente={consultarBloqueio}
                                desabilitado={salvando}
                            />
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter px={{ base: 3, md: 6 }}>
                    <Flex w="100%" gap={2} flexWrap="wrap" justify={{ base: 'stretch', md: 'flex-end' }}>
                        {etapa === 'revisao' && (
                            <Button variant="ghost" onClick={() => setEtapa('dados')} isDisabled={ocupado} flex={{ base: '1 1 auto', md: '0 0 auto' }}>Voltar</Button>
                        )}
                        <Button variant="ghost" onClick={onClose} isDisabled={ocupado} flex={{ base: '1 1 auto', md: '0 0 auto' }}>Cancelar</Button>
                        {precisaFotos && etapa === 'dados' ? (
                            <Button
                                colorScheme="orange"
                                onClick={handleContinuar}
                                isLoading={preparando}
                                loadingText="Preparando"
                                isDisabled={!podeConfirmar || ocupado}
                                flex={{ base: '1 1 auto', md: '0 0 auto' }}
                            >
                                Continuar
                            </Button>
                        ) : (
                            <Button
                                colorScheme="orange"
                                leftIcon={<Icon as={FaWarehouse} />}
                                onClick={handleConfirmar}
                                isLoading={salvando}
                                isDisabled={!podeConfirmar || ocupado || (precisaFotos && carregandoBloqueio)}
                                flex={{ base: '1 1 auto', md: '0 0 auto' }}
                            >
                                Confirmar entrada
                            </Button>
                        )}
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>

        <EditorImagemModal
            isOpen={!!imagemEditando}
            imagem={imagemEditando}
            onClose={() => setImagemEditando(null)}
            onSalvo={handleEditada}
        />
        </>
    );
};

export default EntradaEstoqueProblemaModal;
